import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';

// PAGE_WIDTH and TEXT_COLUMN_WIDTH match the layout of termsAndConditions.pdf.
// Expressed as percentages so the overlay scales with the rendered page width
// regardless of zoom level. If the PDF is redesigned, update these values.
// if the PDF text block width changes, measure it and get page width
// from the rpv-core__text-layer class in dev tools -> computed width
const TEXT_COLUMN_WIDTH = 662;
const PAGE_WIDTH = 868;
const TEXT_COLUMN_WIDTH_PERCENT = `${(TEXT_COLUMN_WIDTH / PAGE_WIDTH) * 100}%`;
const TEXT_COLUMN_LEFT_PERCENT = `${((PAGE_WIDTH - TEXT_COLUMN_WIDTH) / 2 / PAGE_WIDTH) * 100}%`;

// The overlay's pixel geometry (top offset, font size) was calibrated against a
// page rendered at this width. The PDF renders at SpecialZoomLevel.PageWidth, so
// its rendered width — and the blank top margin the overlay sits in — shrinks on
// narrow viewports while a fixed-px overlay would not, overflowing into the page
// text. Normalising against this reference lets the overlay track the PDF zoom.
export const PDF_HEADER_REFERENCE_WIDTH = PAGE_WIDTH;

// How much to scale the overlay so it tracks the PDF's own zoom: 1 at the
// reference width (desktop unchanged), proportionally smaller as the page narrows.
// Falls back to 1 before the page width is measured or for a non-positive width.
export const getPdfHeaderScale = (renderedPageWidth: number | undefined): number =>
  renderedPageWidth && renderedPageWidth > 0 ? renderedPageWidth / PDF_HEADER_REFERENCE_WIDTH : 1;

export const TermsPdfPageOneHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'scale',
})<{ scale: number }>(({ theme, scale }) => {
  const { fontSize } = theme.typography['body.md'];
  return {
    position: 'absolute',
    top: `calc(${theme.spacing(3.75)} * ${scale})`,
    left: TEXT_COLUMN_LEFT_PERCENT,
    width: TEXT_COLUMN_WIDTH_PERCENT,
    zIndex: 1,
    // Scale the body.md font with the PDF zoom so the overlay never outgrows the
    // shrinking top margin. lineHeight is unitless (24px / 16px at the reference)
    // so it follows the scaled font instead of staying fixed.
    '& .MuiTypography-root': {
      fontSize: `calc(${fontSize} * ${scale})`,
      lineHeight: 1.5,
    },
  };
});

export const TermsDialogTitle = styled(DialogTitle)(({ theme }) => {
  const { fontFamily, fontWeight, fontSize, lineHeight, letterSpacing } = theme.typography['gazpacho-900-19'];
  return {
    fontFamily,
    fontWeight,
    fontSize,
    lineHeight,
    letterSpacing,
    color: theme.novus.primaryBrandBlue,
    backgroundColor: theme.novus.accentCyanBright,
  };
});

export const TermsDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: 0,
  height: '60vh',
  [theme.breakpoints.down('sm')]: {
    height: '100%',
  },
  '& .rpv-core__annotation-layer': {
    zIndex: 2,
  },
  '& .rpv-core__text-layer': {
    zIndex: 1,
  },
  '& .rpv-core__annotation--link a': {
    cursor: 'pointer',
  },
  '& .rpv-core__page-layer::after': {
    boxShadow: 'none',
  },
  // Remove the 8px default inter-page padding added by the viewer
  '& .rpv-core__inner-page': {
    padding: '0 !important',
  },
  // Shift the page canvas up inside its clipped container to hide the PDF top margin.
  // The translateY percentage is relative to the element's own height, so it scales
  // correctly at any zoom level. The CSS variable is set per-page via pageLayout.buildPageStyles.
  '& .rpv-core__page-layer': {
    transform: 'translateY(var(--page-top-shift, 0%))',
  },
}));

export const TermsDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: '16px 24px',
  gap: '12px',
  boxShadow: theme.boxShadows?.box,
  zIndex: 1,
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    alignItems: 'stretch',
    // `&&` doubles the emotion class (specificity 0,2,0) so this reset beats MUI's
    // own `& > :not(style) ~ :not(style) { margin-left: 8px }` (0,1,2), which would
    // otherwise leave the Tooltip-wrapped ACCEPT button offset 8px when stacked.
    '&& > *': { marginLeft: 0, width: '100%' },
    '& .MuiButton-root': { width: '100%' },
  },
}));
